import type { GapRow } from '@type/store';

/** Canonical status order — Completed last (filters, forms, grouping). */
export const GAP_STATUSES = ['In Progress', 'Achieved', 'Planned', 'Completed'] as const;

/** KPI card order after Total. */
export const GAP_KPI_STATUSES = ['Completed', 'In Progress', 'Achieved', 'Planned'] as const;

/** Gender Specialist module — hide Planned & Completed KPI cards. */
export const GAP_KPI_STATUSES_SPECIALIST = ['In Progress', 'Achieved'] as const;

export type GapStatus = typeof GAP_STATUSES[number];

export interface GapStatusMeta {
  kpiColor: string;
  kpiIcon: string;
  columnClass: string;
  cardClass: string;
  accent: string;
  softBg: string;
  border: string;
}

export const GAP_STATUS_META: Record<string, GapStatusMeta> = {
  'In Progress': {
    kpiColor: 'kc5',
    kpiIcon: '/PIU-ICON/5.svg',
    columnClass: 'gap-col-progress',
    cardClass: 'gap-card-progress',
    accent: '#2563EB',
    softBg: '#EFF6FF',
    border: '#93C5FD',
  },
  Achieved: {
    kpiColor: 'kc2',
    kpiIcon: '/PIU-ICON/4.svg',
    columnClass: 'gap-col-achieved',
    cardClass: 'gap-card-achieved',
    accent: '#059669',
    softBg: '#ECFDF5',
    border: '#6EE7B7',
  },
  Completed: {
    kpiColor: 'kc1',
    kpiIcon: '/PIU-ICON/2.svg',
    columnClass: 'gap-col-completed',
    cardClass: 'gap-card-completed',
    accent: '#047857',
    softBg: '#ECFDF5',
    border: '#34D399',
  },
  Planned: {
    kpiColor: 'kc6',
    kpiIcon: '/PIU-ICON/6.svg',
    columnClass: 'gap-col-planned',
    cardClass: 'gap-card-planned',
    accent: '#6B7280',
    softBg: '#F9FAFB',
    border: '#D1D5DB',
  },
};

export const GAP_COLUMNS = {
  act: 'GAP Activities',
  ind: 'Indicators and Targets',
  resp: 'Responsibility',
  time: 'Time Frame',
  prog: 'Progress',
  status: 'Status',
} as const;

export interface GapModStats {
  total: number;
  inProgress: number;
  achieved: number;
  completed: number;
  planned: number;
}

export interface GapFilterOptions {
  search?: string;
  status?: string;
}

export function normalizeGapStatus(raw: unknown): string {
  const s = String(raw ?? '').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  if (/^in progress$/i.test(s)) return 'In Progress';
  if (/^completed/i.test(s)) return 'Completed';
  if (/^achieved$/i.test(s)) return 'Achieved';
  if (/^planned$/i.test(s)) return 'Planned';
  return s;
}

export function isCompletedGapStatus(status: unknown): boolean {
  return /^completed/i.test(String(status || '').trim());
}

export function gapModStats(rows: GapRow[] = []): GapModStats {
  const inProgress = rows.filter((r) => r.status === 'In Progress').length;
  const achieved = rows.filter((r) => r.status === 'Achieved').length;
  const completed = rows.filter((r) => isCompletedGapStatus(r.status)).length;
  const planned = rows.filter((r) => r.status === 'Planned').length;
  return {
    total: rows.length,
    inProgress,
    achieved,
    completed,
    planned,
  };
}

export function gapFilterStatuses(rows: GapRow[] = []): string[] {
  const extras = [...new Set(rows.map((r) => r.status).filter(Boolean) as string[])]
    .filter((s) => !GAP_STATUSES.includes(s as GapStatus));
  return [...GAP_STATUSES, ...extras];
}

export function groupGapByStatus(rows: GapRow[] = [], statuses: readonly string[] = GAP_STATUSES): Record<string, GapRow[]> {
  const groups: Record<string, GapRow[]> = Object.fromEntries(statuses.map((s) => [s, []]));
  rows.forEach((row) => {
    let status = String(row.status || 'Planned');
    if (isCompletedGapStatus(status)) status = 'Completed';
    else if (!GAP_STATUSES.includes(status as GapStatus)) {
      if (!groups[status]) groups[status] = [];
      groups[status].push(row);
      return;
    }
    if (!groups[status]) groups[status] = [];
    groups[status].push(row);
  });
  return groups;
}

export function filterGapRows(rows: GapRow[], { search = '', status = '' }: GapFilterOptions = {}): GapRow[] {
  const q = search.toLowerCase();
  return rows.filter((r) => {
    const text = `${r.act} ${r.ind} ${r.resp} ${r.prog} ${r.status}`.toLowerCase();
    const matchStatus = !status
      || r.status === status
      || (status === 'Completed' && isCompletedGapStatus(r.status));
    return (!q || text.includes(q)) && matchStatus;
  });
}
